import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button, Input, Select, Card, Pill, PageHeader } from '../lib/ui';
import AiTag from '../components/AiTag';
import { NotesCard, RemindersCard } from '../components/TaskNotes';

// ---------------- API ----------------
const BASE_URL = process.env.REACT_APP_BASE_URL;

const axiosClient = axios.create({
  baseURL: `${BASE_URL}`,
  headers: { 'Content-Type': 'application/json' },
});

axiosClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

axiosClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) window.location.href = '/login';
    return Promise.reject(err);
  }
);

// ---------------- Date helpers (plain YYYY-MM-DD, local time) ----------------
function todayStr(d = new Date()) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return todayStr(new Date(y, m - 1, d + n));
}
function formatNice(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}
function formatShort(dateInput) {
  return new Date(dateInput).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ---------------- Task row (shared between "today" and "upcoming") ----------------
function TaskRow({ task, onToggle, onDelete, showDate }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <input type="checkbox" checked={false} onChange={onToggle} style={{ width: 18, height: 18, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{task.title}<AiTag record={task} /></div>
        {task.notes && <div className="muted" style={{ fontSize: 13 }}>{task.notes}</div>}
      </div>
      {showDate && (
        <span className="muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
          {task.mode === 'on' ? 'on' : 'by'} {formatShort(task.dueDate)}
        </span>
      )}
      {task.overdue && <Pill tone="bad">Overdue</Pill>}
      <button
        onClick={onDelete} aria-label="Delete task"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16, padding: 4, flexShrink: 0 }}
      >✕</button>
    </div>
  );
}

// ---------------- Main page ----------------
export default function TaskManager() {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState(todayStr());
  const [newMode, setNewMode] = useState('by');
  const [newNotes, setNewNotes] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async (d) => {
    setLoading(true); setError('');
    try {
      const res = await axiosClient.get('/planner/today', { params: { date: d } });
      setData(res.data);
    } catch (e) {
      setError('Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(date); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRecurring = async (rule) => {
    setData((d) => ({ ...d, recurring: d.recurring.map((r) => (r.id === rule.id ? { ...r, done: !r.done } : r)) }));
    try {
      if (rule.done) await axiosClient.delete(`/planner/recurring/${rule.id}/complete`, { params: { date } });
      else await axiosClient.post(`/planner/recurring/${rule.id}/complete`, { date });
    } catch (e) {
      load(date);
    }
  };

  const toggleManualDone = async (task) => {
    setData((d) => ({ ...d, manualTasks: d.manualTasks.filter((t) => t._id !== task._id) }));
    try {
      await axiosClient.patch(`/planner/manual/${task._id}`, { completed: true });
    } catch (e) {
      load(date);
    }
  };

  const deleteManual = async (task) => {
    setData((d) => ({ ...d, manualTasks: d.manualTasks.filter((t) => t._id !== task._id) }));
    try {
      await axiosClient.delete(`/planner/manual/${task._id}`);
    } catch (e) {
      load(date);
    }
  };

  const addTask = async () => {
    if (!newTitle.trim() || !newDue) return;
    setAdding(true);
    try {
      const res = await axiosClient.post('/planner/manual', {
        title: newTitle.trim(), notes: newNotes.trim(), dueDate: newDue, mode: newMode,
      });
      setData((d) => ({
        ...d,
        manualTasks: [...d.manualTasks, res.data].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)),
      }));
      setNewTitle(''); setNewNotes(''); setNewMode('by'); setNewDue(todayStr()); setShowAdd(false);
    } catch (e) {
      setError('Failed to add task.');
    } finally {
      setAdding(false);
    }
  };

  const { dueNow, upcoming } = useMemo(() => {
    if (!data) return { dueNow: [], upcoming: [] };
    const due = [], later = [];
    for (const t of data.manualTasks) {
      const dueDate = t.dueDate.slice(0, 10); // ISO -> YYYY-MM-DD
      const isDueNow = (t.mode === 'on' && dueDate === date) || (t.mode === 'by' && dueDate <= date);
      const row = { ...t, overdue: t.mode === 'by' && dueDate < date };
      (isDueNow ? due : later).push(row);
    }
    later.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    return { dueNow: due, upcoming: later };
  }, [data, date]);

  if (loading && !data) return <div className="dashboard-container"><p className="loading">Loading tasks…</p></div>;
  if (error && !data) return <div className="dashboard-container"><p className="error">{error}</p></div>;
  if (!data) return null;

  return (
    <div className="dashboard-container" style={{ maxWidth: 760, margin: '0 auto' }}>
      <PageHeader
        title="Tasks"
        subtitle={formatNice(date)}
        actions={(
          <div className="button-group">
            <button onClick={() => setDate((d) => addDays(d, -1))}>← Prev</button>
            <button onClick={() => setDate(todayStr())} className={date === todayStr() ? 'active' : ''}>Today</button>
            <button onClick={() => setDate((d) => addDays(d, 1))}>Next →</button>
          </div>
        )}
      />

      {/* Today's checklist: recurring rules + subscriptions due */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Today's checklist</h3>
        {data.recurring.length === 0 && data.subscriptions.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Nothing scheduled for this day.</p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {data.recurring.map((r) => (
              <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={r.done} onChange={() => toggleRecurring(r)} style={{ width: 18, height: 18 }} />
                <span style={{ textDecoration: r.done ? 'line-through' : 'none', opacity: r.done ? 0.55 : 1 }}>{r.label}</span>
              </label>
            ))}
            {data.subscriptions.length > 0 && (
              <div style={{
                marginTop: data.recurring.length ? 4 : 0, padding: '10px 12px',
                background: 'var(--panel-2)', borderRadius: 'var(--r-lg)',
              }}>
                <strong>{data.subscriptions.length} subscription{data.subscriptions.length !== 1 ? 's' : ''} due</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {data.subscriptions.map((s) => <Pill key={s.id} tone="blue">{s.name}</Pill>)}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Seeding recommendations */}
      {data.seedingRecommendations.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Seeding recommendations</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {data.seedingRecommendations.map((r) => (
              <div key={r.cropName} style={{
                display: 'flex', justifyContent: 'space-between', gap: 12,
                padding: '8px 0', borderBottom: '1px solid var(--border)',
              }}>
                <strong style={{ flexShrink: 0 }}>{r.cropName}</strong>
                <span className="muted" style={{ fontSize: 13, textAlign: 'right' }}>{r.reason}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Manual tasks due today / overdue, + add form */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Your tasks</h3>
          <Button variant="primary" onClick={() => setShowAdd((v) => !v)}>{showAdd ? 'Cancel' : '+ Add task'}</Button>
        </div>

        {showAdd && (
          <div style={{ display: 'grid', gap: 10, marginTop: 14, padding: 14, background: 'var(--panel-2)', borderRadius: 'var(--r-lg)' }}>
            <Input placeholder="What needs doing?" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} autoFocus />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Input type="date" value={newDue} onChange={(e) => setNewDue(e.target.value)} style={{ width: 'auto' }} />
              <Select value={newMode} onChange={(e) => setNewMode(e.target.value)} style={{ width: 'auto' }}>
                <option value="by">Complete by this date</option>
                <option value="on">Do on this date</option>
              </Select>
            </div>
            <Input placeholder="Notes (optional)" value={newNotes} onChange={(e) => setNewNotes(e.target.value)} />
            <Button
              variant="primary" onClick={addTask} disabled={adding || !newTitle.trim()}
              style={{ justifySelf: 'start' }}
            >
              {adding ? 'Adding…' : 'Add task'}
            </Button>
          </div>
        )}

        <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
          {dueNow.length === 0 && <p className="muted" style={{ margin: 0 }}>Nothing due.</p>}
          {dueNow.map((t) => (
            <TaskRow key={t._id} task={t} onToggle={() => toggleManualDone(t)} onDelete={() => deleteManual(t)} />
          ))}
        </div>
      </Card>

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Upcoming</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {upcoming.map((t) => (
              <TaskRow key={t._id} task={t} onToggle={() => toggleManualDone(t)} onDelete={() => deleteManual(t)} showDate />
            ))}
          </div>
        </Card>
      )}

      {/* Reminders pushed to the phone, and quick notes (from Farm OS, the assistant or a phone shortcut) */}
      <RemindersCard />
      <NotesCard />
    </div>
  );
}
